import { ExclamationCircleIcon } from '@heroicons/react/24/outline'

import { RequestLog } from '@engine/models'
import type { EngineTrackResponse } from '@engine/models/tracking'

import { ActivityTimeline } from '#components/ActivityTimeline'
import { ExpandableJsonPanel } from '#components/ExpandableJsonPanel.js'
import { KeyValueDisplay, KeyValueItem } from '#components/KeyValueDisplay'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'

import { TrackingStatusBadge } from './TrackingStatusBadge.js'

interface TrackingDetailsProps {
  tracking?: EngineTrackResponse | null
}

const TrackingDetails = ({ tracking }: TrackingDetailsProps) => {
  if (!tracking) return null

  // Fetch request logs to check for multiple track/startTrack requests
  const { items: requestLogs } = useInfiniteAdminResource<RequestLog>(
    'request-logs',
    {
      externalId: tracking.externalId,
      limit: 100,
    },
    {
      enabled: !!tracking.externalId,
    },
  )

  // Count track and startTrack request logs
  const trackRequestLogs = requestLogs.filter(
    log => log.type === 'track' || log.type === 'startTrack',
  )

  // Extract unique PRO numbers from tracking requests
  const uniquePros = new Set<string>()
  trackRequestLogs.forEach(log => {
    const pro = log.tenantRequest?.content?.details?.[0]?.trackingNumber
    if (pro && typeof pro === 'string') {
      uniquePros.add(pro)
    }
  })
  const hasMultipleTrackingPros = uniquePros.size > 1
  const origin = tracking.shipmentDetails?.origin
  const destination = tracking.shipmentDetails?.destination

  const items: KeyValueItem[] = [
    {
      label: 'Tracking ID',
      value: tracking._id,
    },
    ...(tracking.externalId
      ? [
          {
            label: 'External ID',
            value: tracking.externalId,
          },
        ]
      : []),
    ...(tracking.trackingNumber
      ? [
          {
            label: 'Tracking Number (PRO)',
            value: tracking.trackingNumber,
          },
        ]
      : []),
    ...(tracking.pickupDate
      ? [
          {
            label: 'Pickup Date',
            value: tracking.pickupDate,
          },
        ]
      : []),
    ...(origin
      ? [
          {
            label: 'Origin',
            value: `${
              origin.city && origin.state
                ? `${origin.city}, ${origin.state}`
                : origin.city || origin.state || ''
            }${origin.postalCode ? ` ${origin.postalCode}` : ''}${
              origin.country && origin.country !== 'US'
                ? ` (${origin.country})`
                : ''
            }`,
            className: 'text-sm',
          },
        ]
      : []),
    ...(destination
      ? [
          {
            label: 'Destination',
            value: `${
              destination.city && destination.state
                ? `${destination.city}, ${destination.state}`
                : destination.city || destination.state || ''
            }${destination.postalCode ? ` ${destination.postalCode}` : ''}${
              destination.country && destination.country !== 'US'
                ? ` (${destination.country})`
                : ''
            }`,
          },
        ]
      : []),
    ...(tracking.tenantId
      ? [
          {
            label: 'Tenant ID',
            value: tracking.tenantId,
          },
        ]
      : []),
    ...(tracking.carrierConnectionId
      ? [
          {
            label: 'Carrier Connection ID',
            value: tracking.carrierConnectionId,
          },
        ]
      : []),
    ...(tracking.carrierCode
      ? [
          {
            label: 'Carrier Code',
            value: tracking.carrierCode.toUpperCase(),
            className: 'uppercase',
          },
        ]
      : []),
    {
      label: 'Status',
      value: <TrackingStatusBadge status={tracking.currentStatus?.status} />,
      className: 'mt-1',
    },
    ...(tracking.lastUpdated
      ? [
          {
            label: 'Last Updated',
            value: new Date(tracking.lastUpdated).toLocaleString(),
          },
        ]
      : []),
    ...(tracking.lastFetched
      ? [
          {
            label: 'Last Fetched',
            value: new Date(tracking.lastFetched).toLocaleString(),
          },
        ]
      : []),
    {
      label: 'Next Run',
      value: tracking.nextRunDate
        ? new Date(tracking.nextRunDate).toLocaleString()
        : 'Not queued',
    },
  ]

  return (
    <div>
      {hasMultipleTrackingPros && (
        <div className="rounded-box border border-info/30 bg-info/10 p-4 mb-6">
          <div className="flex items-start gap-3">
            <ExclamationCircleIcon className="h-6 w-6 text-info" />
            <div className="flex-1">
              <h4 className="font-semibold text-info mb-1">
                Multiple PRO Numbers Associated
              </h4>
              <p className="text-sm text-info/80 mb-2">
                This load has {trackRequestLogs.length} PROs associated with it.
              </p>
              {uniquePros.size > 1 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {Array.from(uniquePros).map(pro => (
                    <span
                      key={pro}
                      className="badge badge-info badge-outline text-xs"
                    >
                      {pro}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <KeyValueDisplay items={items} className="col-span-full" />

      <div className="col-span-full mt-6">
        <ExpandableJsonPanel
          title="Full Tracking Data"
          data={tracking}
          className="col-span-full mb-6"
          preClassName="bg-slate-950/80 border border-base-300 font-mono text-base-content"
        />
        <ActivityTimeline
          externalId={tracking.externalId}
          emptyMessage="No traces or request logs found for this tracking."
        />
      </div>
    </div>
  )
}

export { TrackingDetails }
